import { FeedArea } from '@go-shared/types/feedTemplate';
import { ObjectId } from 'bson';
import {
  DropTargetMonitor,
  DropTargetConnector,
  DragSourceConnector,
  DragSourceMonitor,
} from 'react-dnd';
import { DraggedContentItem } from '@go-client/components/DraggableContentItem';
import { BareContentTemplateLegacy } from '@go-shared/types/contentTemplate';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { Language } from '@go-shared/types/info';
import { Event } from '@go-shared/types/event';
import { makeEventSorter } from '@go-shared/lib/event';
import { Time } from '@go-shared/types/time';
import { timeToMinutes } from '@go-shared/lib/time';

export const createColumn = (): FeedArea => {
  return {
    rectangle: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    style: {},
    className: 'flex flex-col w-full',
    contentType: 'column',
    _id: new ObjectId().toHexString(),
  };
};

export const createRow = (): FeedArea => {
  return {
    rectangle: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    style: {
      width: '100%',
    },
    className: 'inline-flex w-full',
    contentType: 'row',
    _id: new ObjectId().toHexString(),
    children: [createColumn()],
  };
};

export const createText = (): FeedArea => {
  return {
    rectangle: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    style: {},
    className: 'inline-flex border border-red w-full',
    contentType: 'text',
    params: { text: 'Placeholder' },
    _id: new ObjectId().toHexString(),
  };
};

export const hotspotTarget = {
  drop(props: any, monitor: DropTargetMonitor) {
    let draggedItem = monitor.getItem() as DraggedContentItem;

    if (props.onDrop) props.onDrop(draggedItem.contentTemplate);
  },
};

export function collectTarget(connect: DropTargetConnector, monitor: DropTargetMonitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
  };
}

export function collectSource(connect: DragSourceConnector, monitor: DragSourceMonitor) {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  };
}

interface DraggedItem {
  contentTemplate?: BareContentTemplateLegacy;
}

export interface BaseDraggableSourceProps {
  onDragStart?: () => void;
  contentTemplate?: BareContentTemplateLegacy;
  connectDragSource?: any;
}

export interface BaseFeedEditorHotspotProps {
  contentTemplate: BareContentTemplateLegacy;
  area: FeedArea;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  highlightAreas: boolean;
  editorLocked: boolean;
  mcmKey: string;
}

// We extend this interface to ensure that in the component we are using the right props, just a TS
// validation
export function contentDragSource<T extends BaseDraggableSourceProps>() {
  return {
    beginDrag(props: T): DraggedItem {
      let returnedData: { [key: string]: any } = {
        contentTemplate: props.contentTemplate,
      };
      if (props.onDragStart) props.onDragStart();

      return returnedData;
    },
  };
}

export const findContentTemplate = (id: string, cts: BareContentTemplateLegacy[]) =>
  cts.find(ct => ct._id === id);

export const findEventTemplate = (id: string, ets: EventTemplate[]) =>
  ets.find(et => et._id === id);

export const findAllEvents = (template: string, events: Event[], dayStart: Time) => {
  const result = events.filter(e => e.template._id === template);
  result.sort(makeEventSorter(timeToMinutes(dayStart)));
  return result;
};
